import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import notificationsService from '../../services/notificationsService';
import {
  LayoutDashboard,
  Users,
  Building2,
  ClipboardList,
  BarChart3,
  Settings,
  CreditCard,
  Search,
  Bell,
  Globe,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Shield,
  MoreHorizontal,
  FileCheck,
  Video,
  HelpCircle,
  Loader,
  Clock,
  ToggleRight,
  MapPin
} from 'lucide-react';
import './TopNavbar.css';

const TopNavbar = () => {
  const { t, i18n } = useTranslation();
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const moreMenuRef = useRef(null);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isRTL = i18n.language === 'ar';

  // Split nav items - main items and overflow items
  const mainNavItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/users', label: t('nav.users'), icon: Users },
    { path: '/offers', label: t('nav.offers'), icon: Building2 },
    { path: '/requests', label: t('nav.requests'), icon: ClipboardList },
    { path: '/subscriptions', label: t('nav.subscriptions'), icon: CreditCard }
  ];

  const moreNavItems = [
    { path: '/features', label: t('nav.features'), icon: ToggleRight },
    { path: '/locations', label: t('nav.locations', 'مناطق العمل'), icon: MapPin },
    { path: '/notary-offices', label: t('nav.notaryOffices'), icon: FileCheck },
    { path: '/tutorials', label: t('nav.tutorials'), icon: Video },
    { path: '/faqs', label: t('nav.faqs'), icon: HelpCircle },
    { path: '/reports', label: t('nav.reports'), icon: BarChart3 },
    { path: '/settings', label: t('nav.settings'), icon: Settings }
  ];

  const allNavItems = [...mainNavItems, ...moreNavItems];

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setShowMobileMenu(false);
    setShowMoreMenu(false);
  }, [location.pathname]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      const response = await notificationsService.getAll({ limit: 5 });
      if (response.success) {
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  // Fetch notifications on mount and when dropdown opens
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications, fetchNotifications]);

  const formatNotificationTime = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return isRTL ? 'الآن' : 'Now';
    if (diffMins < 60) return isRTL ? `منذ ${diffMins} دقيقة` : `${diffMins}m ago`;
    if (diffHours < 24) return isRTL ? `منذ ${diffHours} ساعة` : `${diffHours}h ago`;
    if (diffDays < 7) return isRTL ? `منذ ${diffDays} يوم` : `${diffDays}d ago`;

    return notifDate.toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if any "more" item is active
  const isMoreActive = moreNavItems.some(item => location.pathname === item.path);

  return (
    <>
      <header className="top-navbar">
        <div className="navbar-container">
          {/* Brand */}
          <NavLink to="/dashboard" className="navbar-brand">
            <div className="brand-icon">
              <Shield size={24} />
            </div>
            <div className="brand-text">
              <span className="brand-title">{t('common.adminPanel')}</span>
              <span className="brand-subtitle">{t('common.appName')}</span>
            </div>
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="navbar-nav desktop-nav">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}

            {/* More Menu */}
            <div className="more-menu-wrapper" ref={moreMenuRef}>
              <button
                className={`nav-link more-btn ${isMoreActive ? 'active' : ''}`}
                onClick={() => setShowMoreMenu(!showMoreMenu)}
              >
                <MoreHorizontal size={18} />
                <span>{t('nav.more')}</span>
                <ChevronDown size={14} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
              </button>

              {showMoreMenu && (
                <div className="more-dropdown">
                  {moreNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) => `more-dropdown-item ${isActive ? 'active' : ''}`}
                        onClick={() => setShowMoreMenu(false)}
                      >
                        <Icon size={18} />
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>

          {/* Actions */}
          <div className="navbar-actions">
            {/* Search */}
            <div className="navbar-search">
              <Search size={18} />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Language Toggle */}
            <button className="action-btn" onClick={toggleLanguage} title={t('settings.language')}>
              <Globe size={20} />
              <span className="lang-text">{i18n.language === 'ar' ? 'EN' : 'عربي'}</span>
            </button>

            {/* Notifications */}
            <div className="notifications-wrapper" ref={notificationsRef}>
              <button
                className={`action-btn notifications-btn ${unreadCount > 0 ? 'has-unread' : ''}`}
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell size={20} />
                {unreadCount > 0 && <span className="badge-count">{unreadCount}</span>}
              </button>

              {showNotifications && (
                <div className="notifications-dropdown">
                  <div className="dropdown-header">
                    <h4>{t('common.notifications')}</h4>
                    {unreadCount > 0 && (
                      <span className="unread-badge">{unreadCount} {isRTL ? 'جديد' : 'new'}</span>
                    )}
                  </div>
                  <div className="notifications-list">
                    {notificationsLoading ? (
                      <div className="notifications-loading">
                        <Loader size={24} className="spin" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="notifications-empty">
                        <Bell size={32} />
                        <p>{isRTL ? 'لا توجد إشعارات' : 'No notifications'}</p>
                      </div>
                    ) : (
                      notifications.map(notification => (
                        <div
                          key={notification._id}
                          className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                        >
                          <div className="notification-content">
                            <span className="notification-title">
                              {isRTL ? notification.titleAr : notification.titleEn}
                            </span>
                            {(notification.bodyAr || notification.bodyEn) && (
                              <p className="notification-message">
                                {isRTL ? notification.bodyAr : notification.bodyEn}
                              </p>
                            )}
                            <span className="notification-time">
                              <Clock size={12} />
                              {formatNotificationTime(notification.createdAt)}
                            </span>
                          </div>
                          {!notification.isRead && <div className="unread-dot" />}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="dropdown-footer">
                    <button className="view-all-btn" onClick={() => { navigate('/notifications'); setShowNotifications(false); }}>{t('common.viewAll')}</button>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="user-menu-wrapper" ref={userMenuRef}>
              <button
                className="user-menu-btn"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  {(admin?.avatarUrl || admin?.avatar) ? (
                    <img src={admin.avatarUrl || admin.avatar} alt={admin.name} />
                  ) : (
                    <span>{admin?.name?.charAt(0)}</span>
                  )}
                </div>
                <ChevronDown size={16} className={`chevron ${showUserMenu ? 'open' : ''}`} />
              </button>

              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="dropdown-user-info">
                    <span className="dropdown-user-name">{admin?.name}</span>
                    <span className="dropdown-user-role">{t('common.superAdmin')}</span>
                  </div>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => navigate('/settings')}>
                    <Settings size={18} />
                    <span>{t('nav.settings')}</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item danger" onClick={handleLogout}>
                    <LogOut size={18} />
                    <span>{t('common.logout')}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-btn"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              aria-label="Toggle menu"
            >
              {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {showMobileMenu && (
          <>
            <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)} />
            <nav className="mobile-nav">
              {allNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          </>
        )}
      </header>
    </>
  );
};

export default TopNavbar;
